import { CheckCircleOutlined } from '@ant-design/icons';
import { Checkbox, Table, Input } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import Page from 'components/page/page';
import PageSection from 'components/page/pageSection';
import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useNewTaskClosing } from './use-new-task-closing';
import { PaymentType } from 'types/payment';
import { OrderType } from 'types/order-type';
import Formatting from 'utils/formatting';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import moment from 'moment';
import { AsyncButton } from 'components';
import { TaskType } from 'types';
import { CloseTaskDto } from '../../API';

const OrdersTable = styled.div`
	height: fit-content;

	> .ant-table-wrapper {
		height: 100%;
	}
`;

interface OrderWithSelection {
	key: string;
	id: number;
	type: OrderType;
	address: string;
	grossPrice: number;
	alreadyPaid: number;
	deliveryDate: string;
	selected: boolean;
}

export const NewTaskClosing = () => {
	const { id: idRaw } = useParams();
	const id = idRaw ? parseInt(idRaw, 10) : undefined;
	const { order, otherOrders, closeTask, task } = useNewTaskClosing(id);
	const [paid, setPaid] = useState(false);
	const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
	const [advance, setAdvance] = useState(false);
	const isAlreadyPaid = order ? order.payments.reduce((acc, p) => acc + p.amount, 0) >= order.grossPrice : false;
	const alreadyPaid = order ? order.payments.reduce((acc, p) => acc + p.amount, 0) : 0;

	const otherOrdersWithSelection = useMemo(() => {
		return (
			otherOrders?.map((order) => ({
				...order,
				key: `${order.id}-${order.type}`,
				selected: selectedOrders.includes(order.id),
			})) ?? []
		);
	}, [otherOrders, selectedOrders]);

	const handleMainOrderChange = (e: CheckboxChangeEvent) => {
		setPaid(e.target.checked);

		// Reset deposit amount when full payment is selected
		if (e.target.checked) {
			setAdvance(false);
		}
	};

	const handleAdvanceChange = (e: CheckboxChangeEvent) => {
		setAdvance(e.target.checked);
	};

	const handleOrderSelectionChange = (orderId: number, checked: boolean) => {
		if (checked) {
			setSelectedOrders((prev) => [...prev, orderId]);
		} else {
			setSelectedOrders((prev) => prev.filter((id) => id !== orderId));
		}
	};

	const totalPaymentSum = useMemo(() => {
		if (!order || !otherOrders) {
			return 0;
		}

		let sum = 0;

		// Add main order amount
		if (paid) {
			sum += order.grossPrice - alreadyPaid;
		} else if (advance && order.wantedAdvance) {
			sum += order.wantedAdvance;
		}

		// Add other selected orders
		otherOrders.forEach((otherOrder) => {
			if (selectedOrders.includes(otherOrder.id)) {
				sum += otherOrder.grossPrice - otherOrder.alreadyPaid;
			}
		});

		return sum;
	}, [order, otherOrders, paid, selectedOrders, advance]);

	const otherOrdersColumns: ColumnsType<OrderWithSelection> = [
		{
			title: 'Opłacone',
			dataIndex: 'selected',
			key: 'selected',
			render: (_, record) => (
				<Checkbox
					checked={selectedOrders.includes(record.id)}
					onChange={(e) => handleOrderSelectionChange(record.id, e.target.checked)}
				/>
			),
		},
		{
			title: 'Adres',
			dataIndex: 'address',
			key: 'address',
		},
		{
			title: 'Cena brutto',
			dataIndex: 'grossPrice',
			key: 'grossPrice',
			render: (_, record) => (
				<div className="flex flex-col items-center">
					{Formatting.money(record.grossPrice)}
					{record.alreadyPaid > 0 && (
						<span className="text-green-500 ml-2 text-sm">(zaliczka: {Formatting.money(record.alreadyPaid)})</span>
					)}
				</div>
			),
		},
		{
			title: 'Data dostawy',
			dataIndex: 'deliveryDate',
			key: 'deliveryDate',
			render: (_, record) => Formatting.epochToDate(record.deliveryDate, moment(record.deliveryDate).hours() === 0),
		},
	];

	const handleCloseTask = async () => {
		if (!order) {
			return;
		}

		const dto: CloseTaskDto = {
			paidOrders: [],
		};

		// Full payment (only for cash payments)
		if (paid && order.wantedPaymentType === PaymentType.cash) {
			dto.paidOrders.push({
				id: order.id,
				type: OrderType.loose,
			});
		}

		// Add all the selected orders from otherOrders
		if (otherOrders && selectedOrders.length > 0) {
			otherOrders.forEach((otherOrder) => {
				if (selectedOrders.includes(otherOrder.id)) {
					dto.paidOrders.push({
						id: otherOrder.id,
						type: otherOrder.type,
					});
				}
			});
		}

		if (advance && order.wantedAdvance) {
			dto.advance = order.wantedAdvance;
		}

		return closeTask(dto);
	};

	const actions = [
		<AsyncButton key="0" icon={<CheckCircleOutlined />} onClick={handleCloseTask}>
			Zrealizuj zadanie
		</AsyncButton>,
	];

	return (
		<Page title="Terminarz / Realizacja zadania" actions={actions}>
			<PageSection title="Wybrane zamówienie">
				{order && (
					<div className="flex flex-dir">
						<div className="text-base">
							Cena brutto: <strong className="text-blue-500">{Formatting.money(order.grossPrice)}</strong>
							{alreadyPaid > 0 && (
								<span className="text-green-500 ml-2">(zaliczka: {Formatting.money(alreadyPaid)})</span>
							)}
						</div>

						<div className="flex flex-col gap-4 p-5 bg-gray-50 rounded-lg mt-4">
							{order.wantedPaymentType === PaymentType.cash ? (
								<div className="flex items-center gap-2">
									<Checkbox disabled={isAlreadyPaid} checked={isAlreadyPaid || paid} onChange={handleMainOrderChange}>
										{alreadyPaid > 0
											? `Klient zapłacił: ${Formatting.money(order.grossPrice - alreadyPaid)}`
											: 'Zamówienie opłacone w całości'}
									</Checkbox>
								</div>
							) : (
								<p className="text-base text-gray-600 m-0">(Płatne przelewem)</p>
							)}

							{!isAlreadyPaid && !paid && task?.type != TaskType.claiming && !order.onPlace && order.wantedAdvance && (
								<div className="flex items-center gap-5 flex-wrap">
									<Checkbox checked={advance} onChange={handleAdvanceChange}>
										Klient wpłacił zaliczkę
									</Checkbox>
									<Input
										type="text"
										placeholder="0.00"
										value={order.wantedAdvance}
										// onChange={handleAdvanceChange}
										suffix="PLN"
										disabled={paid}
										className="!w-fit"
									/>
								</div>
							)}
						</div>
					</div>
				)}
			</PageSection>
			<PageSection title="Zaległe zamówienia">
				{otherOrders && otherOrders.length > 0 ? (
					<>
						<OrdersTable>
							<Table
								columns={otherOrdersColumns}
								dataSource={otherOrdersWithSelection}
								pagination={false}
								rowKey="id"
							/>
						</OrdersTable>
						<div className="mt-5 text-center font-bold text-base p-4 border-t border-gray-200">
							Suma do pobrania: {Formatting.money(totalPaymentSum)} PLN
						</div>
					</>
				) : (
					<p>Brak zaległych zamówień do zapłaty.</p>
				)}
			</PageSection>
		</Page>
	);
};
